/*
  OTR Diagnostics cart pairing.

  The OTR Link 2 adapter (hardware) is only sold with an OTR Diagnostics plan. On the
  product page the otrperformance-production app adds the pair: the adapter first (with
  a `_linked_to` property), then the plan linked back to it (`_ref_variant`). This keeps
  every other path honest:

  1. One add per click on the app's plan form. The theme's own ProductForm also listens
     for the form's submit event and can add a second, unlinked adapter; submits are
     routed to the app's button instead.
  2. Any other Add to Cart for the adapter (search results, collections, quick view,
     a form the app hasn't taken over) goes to the product page so a plan is picked.
  3. Whenever the cart changes, adapters without a plan are taken out, unlinked extras
     first, and a short note says why. The check waits for the app's two-step add to
     finish, so it never removes an adapter whose plan is still on its way.

  Config comes from snippets/otr-diagnostics-pairing.liquid (#OtrPairingConfig).
*/
(() => {
  if (window.otrPairing) return;

  const configElement = document.getElementById('OtrPairingConfig');
  if (!configElement) return;

  let config;
  try {
    config = JSON.parse(configElement.textContent);
  } catch (error) {
    return;
  }

  const HARDWARE_ID = Number(config.hardwareVariantId);
  const PLAN_IDS = new Set((config.planVariantIds || []).map(Number));
  if (!HARDWARE_ID) return;

  // How long a cart has to stay unpaired before anything is removed, and how long an
  // add started from the product page is given to finish.
  const SETTLE_MS = 1500;
  const ADD_WINDOW_MS = 10000;
  const NOTICE_KEY = 'otrPairingNotice';
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  let addInFlightUntil = 0;
  let checkTimer = null;
  let checking = false;

  const isHardwareLine = (item) => item.variant_id === HARDWARE_ID;
  const isPlanLine = (item) =>
    PLAN_IDS.has(item.variant_id) || Number(item.properties && item.properties._ref_variant) === HARDWARE_ID;

  function excessOf(cart) {
    let hardware = 0;
    let plans = 0;
    for (const item of cart.items) {
      if (isHardwareLine(item)) hardware += item.quantity;
      else if (isPlanLine(item)) plans += item.quantity;
    }
    return Math.max(0, hardware - plans);
  }

  async function fetchCart() {
    const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    return response.json();
  }

  // Adapter lines to trim: unlinked ones (no `_linked_to`) first, newest first.
  function trimUpdates(cart, excess) {
    const lines = cart.items
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => isHardwareLine(item))
      .sort((a, b) => {
        const aLinked = a.item.properties && a.item.properties._linked_to ? 1 : 0;
        const bLinked = b.item.properties && b.item.properties._linked_to ? 1 : 0;
        return aLinked - bLinked || b.index - a.index;
      });

    const updates = {};
    let remaining = excess;
    for (const { item } of lines) {
      if (remaining <= 0) break;
      const take = Math.min(item.quantity, remaining);
      updates[item.key] = item.quantity - take;
      remaining -= take;
    }
    return updates;
  }

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const onCartPage = () => document.body.classList.contains('template-cart');

  function refreshCartUi(cart) {
    if (onCartPage()) {
      window.location.reload();
      return;
    }
    if (window.theme && theme.pubsub) {
      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'otr-pairing', cart });
    }
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
  }

  function showNotice() {
    if (document.querySelector('.otr-pairing-toast')) return;

    const toast = document.createElement('div');
    toast.className = 'otr-pairing-toast';
    toast.setAttribute('role', 'status');

    const text = document.createElement('span');
    text.textContent = `${config.notice} `;
    const link = document.createElement('a');
    link.href = config.productUrl;
    link.textContent = config.linkLabel;
    text.appendChild(link);

    const close = document.createElement('button');
    close.type = 'button';
    close.setAttribute('aria-label', 'Dismiss');
    close.textContent = '×';
    close.addEventListener('click', () => toast.remove());

    toast.append(text, close);
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 12000);
  }

  async function check() {
    if (checking) return;
    if (Date.now() < addInFlightUntil) {
      scheduleCheck(addInFlightUntil - Date.now() + 200);
      return;
    }
    checking = true;
    try {
      let cart = await fetchCart();
      if (excessOf(cart) === 0) return;

      // Give an add that is still running time to land its plan, then look again.
      await sleep(SETTLE_MS);
      if (Date.now() < addInFlightUntil) {
        scheduleCheck(addInFlightUntil - Date.now() + 200);
        return;
      }
      cart = await fetchCart();
      const excess = excessOf(cart);
      if (excess === 0) return;

      const response = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: trimUpdates(cart, excess) })
      });
      const updated = await response.json();

      try {
        sessionStorage.setItem(NOTICE_KEY, '1');
      } catch (error) {}
      refreshCartUi(updated);
      showNotice();
    } catch (error) {
      console.error('[OTR pairing]', error);
    } finally {
      checking = false;
    }
  }

  function scheduleCheck(delay = 300) {
    clearTimeout(checkTimer);
    checkTimer = setTimeout(check, delay);
  }

  // A form whose Add to Cart the app manages (it sets `hasPlans` on it).
  const isPlanForm = (form) => form && form.hasPlans === true;
  const addsHardware = (form) => {
    const idInput = form && form.querySelector('[name="id"]');
    return !!idInput && Number(idInput.value) === HARDWARE_ID;
  };
  const planFormAddButton = (form) => form.querySelector('button[name="add"]:not([disabled])');

  // 1 + 2: route every submit of an adapter form.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !addsHardware(form)) return;

      event.preventDefault();
      event.stopPropagation();

      if (isPlanForm(form)) {
        // The app's own button runs its linked add exactly once.
        const button = planFormAddButton(form);
        if (button) {
          addInFlightUntil = Date.now() + ADD_WINDOW_MS;
          button.click();
        }
        return;
      }

      // No plan picker on this form: go pick a plan.
      window.location.href = config.productUrl;
    },
    true
  );

  // The app's button is type="button" and adds on click; note that an add is running
  // so the check waits for its plan instead of removing the adapter.
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest && event.target.closest('button[name="add"], button[type="submit"]');
      if (!button) return;
      const form = button.form || button.closest('form');
      if (!addsHardware(form)) return;

      if (isPlanForm(form)) {
        addInFlightUntil = Date.now() + ADD_WINDOW_MS;
        return;
      }
      if (button.type === 'submit') return; // handled by the submit listener above

      // A plain button on a form the app hasn't taken over.
      event.preventDefault();
      event.stopPropagation();
      window.location.href = config.productUrl;
    },
    true
  );

  // 3: check after every cart change.
  const onCartChanged = (data) => {
    if (data && data.source === 'otr-pairing') return;
    // The app publishes this once its two-step add is done.
    if (Date.now() < addInFlightUntil) addInFlightUntil = Date.now();
    scheduleCheck(600);
  };

  function subscribe() {
    if (window.theme && theme.pubsub && theme.pubsub.subscribe) {
      theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, onCartChanged);
    }
    document.addEventListener('cdk:cart:changelinked', () => scheduleCheck(600));
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) scheduleCheck(300);
    });
  }

  function init() {
    subscribe();
    try {
      if (sessionStorage.getItem(NOTICE_KEY)) {
        sessionStorage.removeItem(NOTICE_KEY);
        showNotice();
      }
    } catch (error) {}
    if (Number(config.initialExcess) > 0) scheduleCheck(300);
  }

  window.otrPairing = { check: () => scheduleCheck(0) };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
